import React, { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * CustomCursor — adaptive institutional cursor.
 *
 * Base: 12px outline, spring-eased follow.
 * Hover: 36px on links/buttons, 46px on images. Text inputs yield to caret.
 * Adaptive palette: warm-white on dark sections, deep navy on light sections.
 * Cross-fades over 220 ms. Never invisible.
 *
 * Strictly gated to:
 *   (hover: hover) and (pointer: fine) and (min-width: 1024px)
 *   and (prefers-reduced-motion: no-preference)
 */
const MQ =
  "(hover: hover) and (pointer: fine) and (min-width: 1024px) and (prefers-reduced-motion: no-preference)";

const SIZE = { default: 12, link: 36, image: 46 };
const STROKE = {
  dark: {
    default: "rgba(245,244,242,0.9)",
    link: "rgba(194,161,90,0.7)",
    image: "rgba(194,161,90,0.4)",
  },
  light: {
    default: "rgba(11,19,43,0.75)",
    link: "rgba(11,19,43,0.9)",
    image: "rgba(11,19,43,0.55)",
  },
};

const isTextField = (el) =>
  el &&
  el.matches &&
  (el.matches("textarea") ||
    el.matches('input[type="text"]') ||
    el.matches('input[type="email"]') ||
    el.matches('input[type="search"]') ||
    el.matches('input[type="tel"]') ||
    el.matches('input[type="url"]') ||
    el.matches('input[type="password"]') ||
    el.matches("input:not([type])") ||
    el.matches("[contenteditable]"));

// Detect the scheme (dark / light) at the element under the cursor by walking
// up the ancestor chain, honouring explicit hints first, then falling back to
// sampling the effective background colour.
const parseRGB = (str) => {
  if (!str) return null;
  const m = str.match(/rgba?\s*\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
};
const luminance = ([r, g, b]) =>
  0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);

const detectScheme = (el) => {
  let node = el;
  while (node && node !== document.documentElement) {
    if (node.dataset && node.dataset.cursorScheme) return node.dataset.cursorScheme;
    if (node.dataset && node.dataset.sectionTone === "dark") return "dark";
    node = node.parentElement;
  }
  // Fallback: sample the effective bg colour by walking up until we find a
  // non-transparent bg, then compute luminance.
  node = el;
  while (node && node !== document.documentElement) {
    const cs = window.getComputedStyle(node);
    const rgb = parseRGB(cs.backgroundColor);
    if (rgb && !cs.backgroundColor.includes("rgba(0, 0, 0, 0)")) {
      return luminance(rgb) < 0.5 ? "dark" : "light";
    }
    node = node.parentElement;
  }
  return "light"; // default to light (navy cursor on warm-white)
};

const CustomCursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default");
  const [scheme, setScheme] = useState("light");

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { damping: 30, stiffness: 350, mass: 0.5 });
  const sy = useSpring(y, { damping: 30, stiffness: 350, mass: 0.5 });

  const rafRef = useRef(null);
  const posRef = useRef({ x: -100, y: -100 });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MQ);
    const apply = () => setEnabled(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // rAF-throttled scheme sampler — reads bg under the pointer once per frame
    let tick = 0;
    const sampleScheme = () => {
      rafRef.current = null;
      const { x: px, y: py } = posRef.current;
      const el = document.elementFromPoint(px, py);
      if (el) {
        const s = detectScheme(el);
        setScheme((prev) => (prev === s ? prev : s));
      }
    };

    const move = (e) => {
      posRef.current = { x: e.clientX, y: e.clientY };
      x.set(e.clientX);
      y.set(e.clientY);
      // Throttle scheme sampling to every 3rd frame (~20fps) — cheap
      tick += 1;
      if (tick % 3 === 0 && rafRef.current == null) {
        rafRef.current = window.requestAnimationFrame(sampleScheme);
      }
    };
    const over = (e) => {
      const t = e.target;
      if (isTextField(t) || (t && t.closest && t.closest("[contenteditable]"))) {
        setVariant("text");
        return;
      }
      if (t && t.closest && t.closest('[data-cursor="image"], img')) {
        setVariant("image");
        return;
      }
      if (
        t &&
        t.closest &&
        t.closest('a, button, [data-cursor="target"], [role="button"]')
      ) {
        setVariant("link");
        return;
      }
      setVariant("default");
    };
    // Re-sample on scroll — cursor stays in place, section beneath changes
    const onScroll = () => {
      if (rafRef.current == null) {
        rafRef.current = window.requestAnimationFrame(sampleScheme);
      }
    };
    window.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("mouseover", over, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    document.documentElement.classList.add("cursor-hidden");
    return () => {
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      document.documentElement.classList.remove("cursor-hidden");
    };
  }, [enabled, x, y]);

  if (!enabled || variant === "text") return null;

  const size = SIZE[variant];
  const stroke = STROKE[scheme][variant];

  return (
    <motion.div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        translateX: sx,
        translateY: sy,
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        borderRadius: "50%",
        border: `1px solid ${stroke}`,
        pointerEvents: "none",
        zIndex: 9999,
        transition:
          "width 220ms cubic-bezier(0.16,1,0.3,1), height 220ms cubic-bezier(0.16,1,0.3,1), border-color 240ms ease, margin 220ms cubic-bezier(0.16,1,0.3,1)",
        willChange: "transform, width, height",
        mixBlendMode: "normal",
      }}
    />
  );
};

export default CustomCursor;
